import React, { useState, useMemo, useEffect } from 'lib/react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'lib/redux';
import {
  EVGrid,
  EVTypography,
  EVLinearProgress,
  EVCheckbox,
  EVExpandMore,
  EVExpandLess,
} from 'lib/components';
import Typography from 'commonComponents/typography';
import clsx from 'clsx';
import { getFormatedDeliveryOption, getPrimaryProductByIdFromUserProducts } from 'features/order/Order.utils';
import {
  ADDON_SUBSCRIPTION_PRODUCTS,
  PRIMARY_SUBSCRIPTION_PRODUCTS,
  REGULAR_DELIVERY_PRODUCT_ID,
  getSubscriptionEsxPrice,
} from 'features/order/Order.constants';
import { renderSubscriptionIconWithOne } from 'features/order/SubscriptionIcon.utils';
import { updateSelectedProduct, updateSelectedOtherProducts } from 'features/order/Order.actions';
import { PRODUCT_IDS } from 'constants/AppConstants';
import { ESX_COMPATIBLE_PRODUCTS } from './ProductList.constants';
import ProductSelectionSubscriptionStyles from './ProductSelectionSubscriptionStyles';

function useSubscriptionProductsFromRedux() {
  const primaryProducts = useSelector(({ order }) => order.primarySubscriptionProductsWithUsage ?? []);
  return useMemo(() => {
    const subscription = [];
    const nonSubscription = [];
    primaryProducts.filter((p) => p.isProductActive).forEach((p) => {
      const card = {
        id: p.productId,
        name: p.name,
        subscriptionSkuName: p.subscriptionSkuName ?? '',
        description: p.description || '',
        unitCount: p.unitCount || '',
        unitBadgeCount: p.unitBadgeCount ?? 1,
        price: p.price || '',
        usedQty: p.usedQty ?? 0,
        includedQty: p.includedQty ?? 0,
        overageRatePerUnit: p.overageRatePerUnit ?? 0,
        isSubscription: p.isSubscription,
        allowedDeliveries: p.allowedDeliveries ?? [],
        isEsxCompatible: p.isEsxCompatible ?? false,
        esxPrice: p.esxPrice ?? '',
      };
      if (p.isSubscription) subscription.push(card);
      else nonSubscription.push(card);
    });
    return { subscriptionProducts: subscription, nonSubscriptionProducts: nonSubscription };
  }, [primaryProducts]);
}

function BundlePlanBar({ bundleUsage, classes, children }) {
  const { used, total } = bundleUsage;
  const hasBar = total > 0;
  const usagePercent = hasBar ? Math.round((used / total) * 100) : 0;
  const barValue = Math.min(usagePercent, 100);
  return (
    <EVGrid className={classes.subscriptionProductBlock}>
      {hasBar && (
        <EVGrid className={classes.productUsageBarWrap}>
          <EVTypography className={classes.productUsageBarHeading}>
            Bundle Plan
          </EVTypography>
          <EVGrid className={classes.productUsageBarRow}>
            <EVTypography className={classes.productUsageBarLabel}>
              {used}
              {' / '}
              {total}
              {' units'}
            </EVTypography>
          </EVGrid>
          <EVLinearProgress
            variant="determinate"
            value={barValue}
            className={clsx(
              classes.productUsageBar,
              used >= total && classes.progressBarFull,
            )}
          />
          <EVTypography variant="caption" className={classes.productUsagePercent}>
            {usagePercent}
            % Usage
          </EVTypography>
        </EVGrid>
      )}
      {children}
    </EVGrid>
  );
}
BundlePlanBar.propTypes = {
  bundleUsage: PropTypes.shape({ used: PropTypes.number, total: PropTypes.number }).isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  children: PropTypes.node.isRequired,
};

function SubscriptionProductWithBar({ product, renderCard, classes }) {
  const used = product.usedQty ?? 0;
  const total = product.includedQty ?? 0;
  const hasBar = total > 0;
  const usagePercent = hasBar ? Math.round((used / total) * 100) : 0;
  const barValue = Math.min(usagePercent, 100);

  return (
    <EVGrid className={classes.subscriptionProductBlock}>
      {hasBar && (
        <EVGrid className={classes.productUsageBarWrap}>
          <EVTypography className={classes.productUsageBarHeading}>
            {product.subscriptionSkuName || 'Single Product'}
          </EVTypography>
          <EVGrid className={classes.productUsageBarRow}>
            <EVTypography className={classes.productUsageBarLabel}>
              {used}
              {' / '}
              {total}
              {' units'}
            </EVTypography>
          </EVGrid>
          <EVLinearProgress
            variant="determinate"
            value={barValue}
            className={clsx(
              classes.productUsageBar,
              used >= total && classes.progressBarFull,
            )}
          />
          <EVTypography variant="caption" className={classes.productUsagePercent}>
            {usagePercent}
            % Usage
          </EVTypography>
        </EVGrid>
      )}
      {renderCard(product, true)}
    </EVGrid>
  );
}
SubscriptionProductWithBar.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subscriptionSkuName: PropTypes.string,
    usedQty: PropTypes.number,
    includedQty: PropTypes.number,
  }).isRequired,
  renderCard: PropTypes.func.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
};

function ProductSelectionSubscription() {
  const classes = ProductSelectionSubscriptionStyles();
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [didAutoExpandDefault, setDidAutoExpandDefault] = useState(false);
  const [selectedDeliveryByProduct, setSelectedDeliveryByProduct] = useState({});
  const { subscriptionProducts, nonSubscriptionProducts } = useSubscriptionProductsFromRedux();
  const subscriptionPlanMode = useSelector(({ order }) => order.subscriptionPlanMode);
  const bundleUsage = useSelector(({ order }) => order.bundleUsage);
  const currencyTypeId = useSelector(({ userData }) => userData?.initParameters?.currencyTypeId ?? 1);
  const userProducts = useSelector(({ userData }) => userData?.userProducts);
  const mapPinsCount = useSelector(({ orderAddresses }) => orderAddresses?.mapPoints?.length ?? 0);
  const {
    selectedPrimaryProduct,
    selectedPrimaryDeliveryProduct,
    selectedOtherProducts,
  } = useSelector(({ order }) => ({
    selectedPrimaryProduct: order.selectedPrimaryProduct,
    selectedPrimaryDeliveryProduct: order.selectedPrimaryDeliveryProduct,
    selectedOtherProducts: order.selectedOtherProducts ?? {},
  }));
  const dispatch = useDispatch();
  const isBundlePlan = subscriptionPlanMode === 'bundle' && bundleUsage && bundleUsage.total > 0;
  const unitMultiplier = Math.max(Number(mapPinsCount) || 0, 1);

  // If a primary product is pre-selected (Products API returns IsDefault=true),
  // auto-expand that card once so the UI reflects the selection (similar to old flow radio selection).
  useEffect(() => {
    const selectedId = selectedPrimaryProduct?.ProductID;
    if (!selectedId) {
      setDidAutoExpandDefault(false);
      return;
    }
    if (!didAutoExpandDefault) {
      setExpandedCardId(selectedId);
      setDidAutoExpandDefault(true);
    }
  }, [didAutoExpandDefault, selectedPrimaryProduct?.ProductID]);

  const getOveragePriceDisplay = (product) => {
    const baseUnits = Number(product?.unitBadgeCount ?? 1) || 1;
    const units = baseUnits * unitMultiplier;
    const rate = Number(product?.overageRatePerUnit);
    if (!Number.isFinite(rate) || rate <= 0) return product?.price ?? '';
    const total = units * rate;
    return Number.isInteger(total) ? String(total) : total.toFixed(2);
  };

  // Sync local delivery state from Redux when primary product is set (Number() so 8 and '8' both mean Regular)
  useEffect(() => {
    if (selectedPrimaryProduct?.ProductID && selectedPrimaryDeliveryProduct != null) {
      setSelectedDeliveryByProduct((prev) => ({
        ...prev,
        [selectedPrimaryProduct.ProductID]: Number(selectedPrimaryDeliveryProduct) === REGULAR_DELIVERY_PRODUCT_ID ? '' : selectedPrimaryDeliveryProduct,
      }));
    }
  }, [selectedPrimaryProduct?.ProductID, selectedPrimaryDeliveryProduct]);

  // Payload to clear walls and addons when primary product (or its delivery) changes
  const primarySelectionResetPayload = {
    selectedWallsProduct: null,
    selectedWallsDeliveryProduct: null,
    selectedOtherProducts: {},
  };

  const getDefaultDeliveryForProduct = (fullProduct) => {
    const web = fullProduct?.WebDeliveryProducts;
    if (!web?.length) return REGULAR_DELIVERY_PRODUCT_ID;
    const defaultDelivery = web.find((d) => d.IsDefault)?.ProductID ?? web[0]?.ProductID;
    return defaultDelivery ?? REGULAR_DELIVERY_PRODUCT_ID;
  };

  const handleCardClick = (cardId) => () => {
    const isCollapsingSelectedCard = expandedCardId === cardId
      && selectedPrimaryProduct?.ProductID === cardId;
    if (isCollapsingSelectedCard) {
      setExpandedCardId(null);
      dispatch(updateSelectedProduct({
        selectedPrimaryProduct: null,
        selectedPrimaryDeliveryProduct: null,
        ...primarySelectionResetPayload,
      }));
      return;
    }

    setExpandedCardId((current) => (current === cardId ? null : cardId));
    const fullProduct = getPrimaryProductByIdFromUserProducts(userProducts, cardId);
    if (fullProduct) {
      const defaultDeliveryId = getDefaultDeliveryForProduct(fullProduct);
      dispatch(updateSelectedProduct({
        selectedPrimaryProduct: fullProduct,
        selectedPrimaryDeliveryProduct: defaultDeliveryId,
        ...primarySelectionResetPayload,
      }));
      setSelectedDeliveryByProduct((prev) => ({
        ...prev,
        [cardId]: defaultDeliveryId === REGULAR_DELIVERY_PRODUCT_ID ? '' : defaultDeliveryId,
      }));
    }
  };

  const handleDeliveryChange = (productId, deliveryProductId) => {
    const deliveryIdForRedux = deliveryProductId === '' || deliveryProductId == null
      ? REGULAR_DELIVERY_PRODUCT_ID
      : Number(deliveryProductId);
    setSelectedDeliveryByProduct((prev) => ({ ...prev, [productId]: deliveryProductId }));
    const fullProduct = getPrimaryProductByIdFromUserProducts(userProducts, productId);
    if (!fullProduct) return;
    const isSwitchingPrimaryProduct = selectedPrimaryProduct?.ProductID !== productId;
    const payload = {
      selectedPrimaryProduct: fullProduct,
      selectedPrimaryDeliveryProduct: deliveryIdForRedux ?? REGULAR_DELIVERY_PRODUCT_ID,
      ...(isSwitchingPrimaryProduct ? primarySelectionResetPayload : {}),
    };
    // When only changing delivery (same product), preserve add-ons. ESX can only use regular delivery.
    if (!isSwitchingPrimaryProduct && selectedOtherProducts != null) {
      const hasEsx = Object.hasOwn(selectedOtherProducts, PRODUCT_IDS.ESX);
      payload.selectedOtherProducts = hasEsx
        ? { ...selectedOtherProducts, [PRODUCT_IDS.ESX]: REGULAR_DELIVERY_PRODUCT_ID }
        : { ...selectedOtherProducts };
    }
    dispatch(updateSelectedProduct(payload));
  };

  const isEsxChecked = (productId) => Boolean(
    selectedPrimaryProduct?.ProductID === productId && selectedOtherProducts[PRODUCT_IDS.ESX],
  );

  const handleEsxChange = (product, checked) => {
    const fullProduct = getPrimaryProductByIdFromUserProducts(userProducts, product.id);
    if (checked) {
      setSelectedDeliveryByProduct((prev) => ({ ...prev, [product.id]: '' }));
      if (fullProduct) {
        dispatch(updateSelectedProduct({
          selectedPrimaryProduct: fullProduct,
          selectedPrimaryDeliveryProduct: REGULAR_DELIVERY_PRODUCT_ID,
        }));
      }
      dispatch(updateSelectedOtherProducts({
        ...selectedOtherProducts,
        [PRODUCT_IDS.ESX]: REGULAR_DELIVERY_PRODUCT_ID,
      }));
    } else {
      const next = { ...selectedOtherProducts };
      delete next[PRODUCT_IDS.ESX];
      dispatch(updateSelectedOtherProducts(next));
    }
  };

  const stopRowEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const toggleEsxAddon = (e, product, addonProductId) => {
    stopRowEvent(e);
    if (addonProductId !== PRODUCT_IDS.ESX) return;
    handleEsxChange(product, !isEsxChecked(product.id));
  };

  const handleDeliveryToggle = (e, productId, deliveryProductId, isChecked) => {
    stopRowEvent(e);
    if (isEsxChecked(productId)) return;
    handleDeliveryChange(productId, isChecked ? '' : deliveryProductId);
  };

  const renderCardPriceOrUnits = (product, isSubscription) => {
    if (!isSubscription) {
      return (
        <EVTypography className={classes.productCardPrice}>
          $
          {product.price}
        </EVTypography>
      );
    }

    const includedQty = Number(product.includedQty ?? 0);
    const usedQty = Number(product.usedQty ?? 0);
    const isOverage = includedQty > 0 && usedQty >= includedQty;

    if (isOverage) {
      return (
        <EVTypography className={classes.productCardPrice}>
          $
          {getOveragePriceDisplay(product)}
        </EVTypography>
      );
    }

    const units = (Number(product.unitBadgeCount ?? 1) || 1) * unitMultiplier;
    return (
      <span className={classes.subscriptionCardUnitTag}>
        {units}
        {' '}
        Unit
        {units !== 1 ? 's' : ''}
      </span>
    );
  };

  const getAddonPriceText = (product, addon) => {
    const esxPrice = getSubscriptionEsxPrice(product);
    if (esxPrice > 0) return ` + $${esxPrice}`;
    if (addon.price) return ` + $${addon.price}`;
    return '';
  };

  const renderExpandedRow = (product) => {
    const isEsxCompatibleByConstant = ESX_COMPATIBLE_PRODUCTS.includes(Number(product.id));
    const hasAddonProducts = product.isEsxCompatible
      && isEsxCompatibleByConstant
      && ADDON_SUBSCRIPTION_PRODUCTS.length > 0;
    const hasDeliveryUpgrades = (product.allowedDeliveries?.length ?? 0) > 0;
    if (!hasAddonProducts && !hasDeliveryUpgrades) return null;

    return (
      <>
        <EVGrid className={classes.expandedDivider} />
        <EVGrid className={classes.addonsRow} onClick={(e) => e.stopPropagation()}>
          {hasAddonProducts && (
            <EVGrid className={classes.addonColumn}>
              <EVTypography className={classes.addonLabel}>
                Add-on
              </EVTypography>
              {ADDON_SUBSCRIPTION_PRODUCTS.map((addon) => (
                <EVGrid
                  key={addon.productId}
                  className={clsx(classes.addonCheckbox, classes.addonCheckboxButton)}
                  container
                  alignItems="center"
                  wrap="nowrap"
                  onClick={(e) => toggleEsxAddon(e, product, addon.productId)}
                >
                  <EVCheckbox
                    size="small"
                    checked={addon.productId === PRODUCT_IDS.ESX ? isEsxChecked(product.id) : false}
                    readOnly
                    color="primary"
                    id={`addon-${product.id}-${addon.productId}`}
                    onClick={(e) => toggleEsxAddon(e, product, addon.productId)}
                  />
                  <Typography
                    variant="body-400"
                    fontColor="charcoal-800"
                    component="label"
                    htmlFor={`addon-${product.id}-${addon.productId}`}
                    style={{ marginLeft: 4, cursor: 'pointer' }}
                    onClick={(e) => toggleEsxAddon(e, product, addon.productId)}
                  >
                    {addon.name}
                    {getAddonPriceText(product, addon)}
                  </Typography>
                </EVGrid>
              ))}
            </EVGrid>
          )}
          {hasDeliveryUpgrades && (
            <EVGrid className={classes.addonColumn}>
              <EVTypography className={classes.addonLabel}>
                Delivery Upgrade
              </EVTypography>
              <EVGrid
                className={classes.deliveryCheckboxGroup}
                container
                direction="column"
                wrap="nowrap"
              >
                {product.allowedDeliveries.map((d) => {
                  const isDeliveryDisabled = isEsxChecked(product.id);
                  const currentDelivery = isDeliveryDisabled ? '' : (selectedDeliveryByProduct[product.id] ?? '');
                  const isChecked = currentDelivery !== '' && Number(currentDelivery) === Number(d.productId);
                  return (
                    <EVGrid
                      key={d.productId}
                      className={clsx(classes.addonCheckbox, isDeliveryDisabled && classes.deliveryDisabled)}
                      container
                      alignItems="center"
                      wrap="nowrap"
                    >
                      <EVCheckbox
                        size="small"
                        checked={isChecked}
                        readOnly
                        color="primary"
                        id={`delivery-${product.id}-${d.productId}`}
                        disabled={isDeliveryDisabled}
                        onClick={(e) => handleDeliveryToggle(e, product.id, d.productId, isChecked)}
                      />
                      <Typography
                        variant="body-400"
                        fontColor="charcoal-800"
                        component="label"
                        htmlFor={`delivery-${product.id}-${d.productId}`}
                        style={{ marginLeft: 4, cursor: isDeliveryDisabled ? 'not-allowed' : 'pointer' }}
                        onClick={(e) => handleDeliveryToggle(e, product.id, d.productId, isChecked)}
                      >
                        {getFormatedDeliveryOption(d.name, d.price, currencyTypeId)}
                      </Typography>
                    </EVGrid>
                  );
                })}
              </EVGrid>
            </EVGrid>
          )}
        </EVGrid>
      </>
    );
  };

  const renderProductCard = (product, isSubscription) => {
    // Bundle plan: API stores one shared SKU string on every row; icons need per-primary catalog SKU
    // (dashboard still uses the single subscription row + full bundle name → top-of-hierarchy icon).
    const catalogPrimary = PRIMARY_SUBSCRIPTION_PRODUCTS.find(
      (p) => Number(p.productId) === Number(product.id),
    );
    const iconSkuName = isBundlePlan && isSubscription && catalogPrimary
      ? catalogPrimary.subscriptionSkuName
      : (product.subscriptionSkuName || product.name);

    const isExpanded = expandedCardId === product.id;
    const cardClass = isSubscription ? classes.subscriptionCard : classes.productCard;
    const contentClass = isSubscription ? classes.subscriptionCardContent : classes.productCardContent;
    const headerClass = isSubscription ? classes.subscriptionCardHeader : classes.productCardTitleRow;
    const titleClass = isSubscription ? classes.subscriptionCardTitle : classes.productCardTitle;
    const rightClass = isSubscription ? classes.cardHeaderRight : classes.productCardRight;
    const descriptionClass = isSubscription
      ? classes.subscriptionCardDescriptionCompact
      : classes.productCardDescription;

    return (
      <EVGrid
        key={product.id}
        className={clsx(cardClass, classes.cardClickable, isExpanded && classes.cardSelected)}
        component="button"
        type="button"
        onClick={handleCardClick(product.id)}
        aria-expanded={isExpanded}
      >
        {renderSubscriptionIconWithOne({
          subscriptionSkuName: iconSkuName,
          iconClassName: classes.catalogIconSvg,
          containerClassName: classes.catalogIconContainer,
          badgeClassName: classes.catalogIconBadge,
          badgeTextClassName: classes.catalogIconBadgeText,
          isSubscriptionProduct: isSubscription,
        })}
        <EVGrid className={contentClass}>
          <EVGrid className={headerClass}>
            <EVTypography className={titleClass}>
              {product.name}
            </EVTypography>
            <EVGrid className={rightClass}>
              {renderCardPriceOrUnits(product, isSubscription)}
              <span className={classes.expandIcon} aria-hidden>
                {isExpanded ? <EVExpandLess /> : <EVExpandMore />}
              </span>
            </EVGrid>
          </EVGrid>
          <EVTypography className={descriptionClass}>
            {product.description}
          </EVTypography>
          {isExpanded && renderExpandedRow(product)}
        </EVGrid>
      </EVGrid>
    );
  };

  return (
    <EVGrid id="order-product-selection-subscription" className={classes.root}>
      {/* SUBSCRIPTION section */}
      <EVGrid className={classes.section}>
        <EVTypography className={classes.sectionHeader}>
          SUBSCRIPTION
        </EVTypography>
        {isBundlePlan ? (
          <BundlePlanBar bundleUsage={bundleUsage} classes={classes}>
            <EVGrid container direction="column" wrap="nowrap" className={classes.subscriptionProductList}>
              {subscriptionProducts.map((product) => (
                <EVGrid key={product.id} item>
                  {renderProductCard(product, true)}
                </EVGrid>
              ))}
            </EVGrid>
          </BundlePlanBar>
        ) : (
          <EVGrid className={classes.subscriptionProductList}>
            {subscriptionProducts.map((product) => (
              <SubscriptionProductWithBar
                key={product.id}
                product={product}
                renderCard={renderProductCard}
                classes={classes}
              />
            ))}
          </EVGrid>
        )}
      </EVGrid>

      {/* NON-SUBSCRIPTION PRODUCTS section */}
      <EVGrid className={classes.section}>
        <EVTypography className={classes.sectionHeader}>
          NON-SUBSCRIPTION PRODUCTS
        </EVTypography>
        <EVGrid className={classes.nonSubscriptionList}>
          {nonSubscriptionProducts.map((product) => renderProductCard(product, false))}
        </EVGrid>
      </EVGrid>
    </EVGrid>
  );
}

export default ProductSelectionSubscription;
